import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  ClipboardCheck, Copy, AlertTriangle, Loader2, CheckCircle2, X, ExternalLink, Shield, GitMerge, Trash2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading, ErrorView } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate } from "@/lib/format";

const TABS = [
  { key: "all", label: "All" },
  { key: "duplicate", label: "Possible Duplicates" },
  { key: "needs_review", label: "Needs Review" },
];

const QUALITY_COLOR = {
  Ace: "bg-emerald-50 text-emerald-700", King: "bg-sky/50 text-navy",
  Queen: "bg-amber-50 text-amber-700", Joker: "bg-slate-100 text-slate-500",
};

function DupBadge() {
  return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><Copy className="h-3 w-3" />Possible duplicate</span>;
}
function NeedsReviewBadge() {
  return <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700"><AlertTriangle className="h-3 w-3" />Needs review</span>;
}

function ResolveDialog({ open, onOpenChange, action, lead, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const meta = {
    not_duplicate: { title: "Mark as not a duplicate", desc: "This clears the duplicate flag and removes the lead from the review queue.", cta: "Confirm — not a duplicate" },
    approve: { title: "Approve lead", desc: "Accept this lead's current qualification and remove it from the review queue.", cta: "Approve" },
    dismiss: { title: "Dismiss from review", desc: "Acknowledge and remove this lead from the review queue without changes.", cta: "Dismiss" },
  }[action] || {};

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/review/${lead.id}/resolve`, { action, note });
      toast.success("Review resolved");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="review-resolve-dialog">
        <DialogHeader>
          <DialogTitle className="font-sora text-navy">{meta.title}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-ink/70">Lead: <strong className="text-navy">{lead?.name || "Unnamed"}</strong></p>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink/60">Note (optional)</label>
            <textarea data-testid="review-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why are you resolving this?" className="w-full rounded-xl border border-sky bg-white px-3 py-2 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="review-resolve-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : meta.cta}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ open, onOpenChange, lead, onDone }) {
  const candidates = lead?.duplicate_of || [];
  // every record involved; the survivor is chosen below, the rest are merged in.
  const records = lead ? [{ id: lead.id, name: lead.name, phone: lead.phone, email: lead.email, stage: lead.stage, owner_name: lead.owner_name, _self: true }, ...candidates] : [];
  const [primaryId, setPrimaryId] = useState("");
  const [assignTo, setAssignTo] = useState("keep");
  const [note, setNote] = useState("");
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrimaryId(candidates[0]?.id || lead?.id || "");
    setAssignTo("keep"); setNote("");
    api.get("/users").then(({ data }) => setUsers((data.data || []).filter((u) => u.status === "Active"))).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sources = records.filter((r) => r.id !== primaryId);

  const submit = async () => {
    if (!primaryId || sources.length === 0) return toast.error("Pick which record to keep.");
    setSaving(true);
    try {
      for (const s of sources) {
        await api.post(`/review/${s.id}/merge`, { target_id: primaryId, assign_to: assignTo, note });
      }
      toast.success(`Merged ${sources.length} duplicate${sources.length > 1 ? "s" : ""} into the kept lead`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="border-sky bg-white sm:max-w-lg" data-testid="review-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><GitMerge className="h-5 w-5 text-loop" />Merge duplicate leads</DialogTitle>
          <DialogDescription>Choose the record to keep. The others are merged into it (notes & activity preserved) and moved to the Recycle Bin.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-xs font-semibold text-ink/60">Keep this record</p>
            <div className="space-y-1.5">
              {records.map((r) => (
                <label key={r.id} data-testid={`merge-keep-${r.id}`} className={`flex cursor-pointer items-center gap-2.5 rounded-xl border p-2.5 text-sm transition-colors ${primaryId === r.id ? "border-loop bg-sky/30" : "border-sky hover:bg-mist"}`}>
                  <input type="radio" name="merge-primary" checked={primaryId === r.id} onChange={() => setPrimaryId(r.id)} className="accent-loop" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-navy">{r.name || "Unnamed"} {r._self && <span className="ml-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">flagged</span>}</p>
                    <p className="truncate text-[11px] text-ink/50">{r.phone || r.email || "—"} · Owner: {r.owner_name || "Unassigned"}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold text-ink/60">Assign the merged lead to</p>
            <select data-testid="merge-assign-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="w-full rounded-xl border border-sky bg-white px-3 py-2 text-sm text-navy outline-none focus:border-loop">
              <option value="keep">Keep current owner of the kept record</option>
              <option value="unassigned">Leave unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <textarea data-testid="merge-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional)" className="w-full rounded-xl border border-sky bg-white px-3 py-2 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="merge-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GitMerge className="h-4 w-4" />Merge {sources.length} into 1</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDuplicateDialog({ open, onOpenChange, lead, onDone }) {
  const candidates = lead?.duplicate_of || [];
  const records = lead ? [{ id: lead.id, name: lead.name, phone: lead.phone, email: lead.email, owner_name: lead.owner_name, _self: true }, ...candidates] : [];
  const [deleteId, setDeleteId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setDeleteId(lead?.id || ""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!deleteId) return toast.error("Pick which record to delete.");
    setSaving(true);
    try {
      await api.delete(`/leads/${deleteId}`, { params: { reason: "Confirmed duplicate (review queue)" } });
      // If we deleted one of the matched records (not the flagged lead), the flagged
      // lead is no longer a duplicate — clear it so it leaves the review queue.
      if (deleteId !== lead.id) {
        await api.post(`/review/${lead.id}/resolve`, { action: "not_duplicate", note: "Duplicate resolved — the other record was deleted" });
      }
      toast.success("Duplicate lead deleted");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="border-sky bg-white sm:max-w-lg" data-testid="review-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><Trash2 className="h-5 w-5 text-red-500" />Delete duplicate lead</DialogTitle>
          <DialogDescription>Choose which record to delete. It moves to the Recycle Bin (restorable); the other record is kept.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-xs font-semibold text-ink/60">Delete this record</p>
            <div className="space-y-1.5">
              {records.map((r) => (
                <label key={r.id} data-testid={`delete-pick-${r.id}`} className={`flex cursor-pointer items-center gap-2.5 rounded-xl border p-2.5 text-sm transition-colors ${deleteId === r.id ? "border-red-300 bg-red-50" : "border-sky hover:bg-mist"}`}>
                  <input type="radio" name="delete-pick" checked={deleteId === r.id} onChange={() => setDeleteId(r.id)} className="accent-red-500" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-navy">{r.name || "Unnamed"} {r._self && <span className="ml-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">flagged</span>}</p>
                    <p className="truncate text-[11px] text-ink/50">{r.phone || r.email || "—"} · Owner: {r.owner_name || "Unassigned"}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="delete-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4" />Delete selected</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewCard({ lead, isAdmin, onResolve, onDelete, onView, onMerge, stageMeta }) {
  const types = lead.review_types || [];
  const qual = lead.qualification;
  const source = [lead.platform, lead.campaign_name].filter(Boolean).join(" · ");
  const stageName = (k) => stageMeta?.[k]?.name || k;
  return (
    <div data-testid={`review-card-${lead.id}`} className="rounded-2xl border border-sky/70 bg-white p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-sora font-semibold text-navy">{lead.name || "Unnamed lead"}</h3>
          <p className="truncate text-xs text-ink/55">{lead.business_name || lead.email || lead.phone || "—"}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-1.5">
          {types.includes("duplicate") && <DupBadge />}
          {types.includes("needs_review") && <NeedsReviewBadge />}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-ink/60">
        {source && <span className="rounded-lg bg-mist px-2 py-1">{source}</span>}
        <span className="rounded-lg bg-mist px-2 py-1">Stage: {lead.stage ? stageName(lead.stage) : "—"}</span>
        <span className="rounded-lg bg-mist px-2 py-1">Owner: {lead.owner_name || "Unassigned"}</span>
        <span className="rounded-lg bg-mist px-2 py-1">{fmtDate(lead.created_at)}</span>
      </div>

      {qual && (
        <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50/50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">{qual.status}</span>
            {qual.quality && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${QUALITY_COLOR[qual.quality] || "bg-slate-100"}`}>{qual.quality}</span>}
            {qual.score != null && <span className="text-[11px] text-ink/50">Score {qual.score}</span>}
          </div>
          {!!(qual.reasons || []).length && (
            <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-ink/70">
              {qual.reasons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
        </div>
      )}

      {!!(lead.duplicate_of || []).length && (
        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50/50 p-3">
          <p className="mb-1.5 text-[11px] font-semibold text-amber-800">Looks similar to:</p>
          <div className="space-y-1.5">
            {lead.duplicate_of.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 text-[12px]">
                <span className="truncate text-navy">{d.name || "Unnamed"} · <span className="text-ink/50">{d.phone || d.email || "—"}</span></span>
                <span className="shrink-0 text-[10px] text-ink/45">{stageName(d.stage)} · {d.owner_name || "Unassigned"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-sky/50 pt-3">
        <button data-testid={`review-view-${lead.id}`} onClick={() => onView(lead)} className="inline-flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-loop hover:bg-sky/30">
          <ExternalLink className="h-3.5 w-3.5" />Open in Leads
        </button>
        <div className="ml-auto flex flex-wrap gap-2">
          {types.includes("duplicate") && (
            <button data-testid={`review-not-duplicate-${lead.id}`} onClick={() => onResolve(lead, "not_duplicate")} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
              <CheckCircle2 className="h-3.5 w-3.5" />Not a duplicate
            </button>
          )}
          {types.includes("duplicate") && !!(lead.duplicate_of || []).length && (
            <button data-testid={`review-merge-${lead.id}`} onClick={() => onMerge(lead)} className="inline-flex items-center gap-1 rounded-lg border border-loop/40 bg-loop/10 px-2.5 py-1.5 text-xs font-semibold text-loop hover:bg-loop/20">
              <GitMerge className="h-3.5 w-3.5" />Merge
            </button>
          )}
          {types.includes("needs_review") && (
            <button data-testid={`review-approve-${lead.id}`} onClick={() => onResolve(lead, "approve")} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
              <CheckCircle2 className="h-3.5 w-3.5" />Approve
            </button>
          )}
          <button data-testid={`review-dismiss-${lead.id}`} onClick={() => onResolve(lead, "dismiss")} className="inline-flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist">
            <X className="h-3.5 w-3.5" />Dismiss
          </button>
          {isAdmin && types.includes("duplicate") && (
            <button data-testid={`review-delete-${lead.id}`} onClick={() => onDelete(lead)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
              Delete duplicate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReviewQueue() {
  const { reloadCounts } = useOutletContext();
  const { user, options } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "Admin";
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resolve, setResolve] = useState(null); // { lead, action }
  const [toDelete, setToDelete] = useState(null);
  const [toMerge, setToMerge] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/review/queue", { params: { type: tab } }); setData(data.data); }
    catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const afterChange = () => { setResolve(null); setToDelete(null); setToMerge(null); load(); reloadCounts?.(); };

  const onView = (lead) => navigate(`/leads?search=${encodeURIComponent(lead.phone || lead.name || "")}`);

  if (loading && !data) return <Loading label="Loading review queue..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="review-page">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><ClipboardCheck className="h-7 w-7 text-loop" />Review Queue</h1>
        <p className="text-sm text-ink/60">Verify possible duplicates and leads that need a human quality check before they move ahead.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const c = t.key === "duplicate" ? counts.duplicate : t.key === "needs_review" ? counts.needs_review : (counts.duplicate || 0) + (counts.needs_review || 0);
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`review-tab-${t.key}`} onClick={() => setTab(t.key)} className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-loop text-white" : "border border-sky bg-white text-ink/70 hover:bg-mist"}`}>
              {t.label}
              {c > 0 && <span className={`rounded-full px-1.5 text-[11px] font-bold ${active ? "bg-white text-loop" : "bg-red-500 text-white"}`}>{c}</span>}
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div data-testid="review-empty" className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-sky bg-white py-16 text-center">
          <Shield className="mb-3 h-10 w-10 text-emerald-500" />
          <p className="font-sora font-semibold text-navy">Nothing to review</p>
          <p className="text-sm text-ink/55">All caught up — no leads are waiting for review.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((lead) => (
            <ReviewCard key={lead.id} lead={lead} isAdmin={isAdmin} onResolve={(l, a) => setResolve({ lead: l, action: a })} onDelete={setToDelete} onView={onView} onMerge={setToMerge} stageMeta={options?.stage_meta} />
          ))}
        </div>
      )}

      {toMerge && (
        <MergeDialog open={!!toMerge} onOpenChange={() => setToMerge(null)} lead={toMerge} onDone={afterChange} />
      )}

      {resolve && (
        <ResolveDialog open={!!resolve} onOpenChange={() => setResolve(null)} action={resolve.action} lead={resolve.lead} onDone={afterChange} />
      )}

      {toDelete && (
        <DeleteDuplicateDialog open={!!toDelete} onOpenChange={() => setToDelete(null)} lead={toDelete} onDone={afterChange} />
      )}
    </div>
  );
}
